import {
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  AdminSpinner,
  Button,
  Div,
  H3,
  Img,
  Modal,
  P,
  PropsModal,
  SuccessToast,
  deleteOrderRecovery,
  moneyCoins,
  orderRecovery,
  useOrders,
  reportError,
} from '@vgrubs/components';
import { MarkAsRecoveredRequestData } from 'components/src/types/api/order.types';
import { Order, RecoveryStatus } from 'components/src/types/order.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

export const RecoveryModal = ({
  modalIsOpen,
  closeModal,
  recovery,
  setData,
  id,
}: PropsModal & {
  modalIsOpen: boolean;
  recovery:
    | {
        amount: number;
        status: RecoveryStatus;
        notes?: string | null;
      }
    | null
    | undefined
    | null;
  setData: Dispatch<SetStateAction<Order | null>>;
  id: number;
}) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const { dataOrders, setDataOrders, dataHistory, setDataHistory } = useOrders(
    (state) => state
  );

  const [selectedRecoveryStatus, setSelectedRecoveryStatus] =
    useState<RecoveryStatus>(recovery?.status || RecoveryStatus.PENDING);
  const [amount, setAmount] = useState(0);

  const statuses = [
    { name: 'Adjustment', value: RecoveryStatus.ADJUSTMENT },
    { name: 'Evidence Needed', value: RecoveryStatus.EVIDENCE_NEEDED },
    { name: 'Not Eligible', value: RecoveryStatus.NOT_ELIGIBLE },
    { name: 'Pending', value: RecoveryStatus.PENDING },
    { name: 'Recovered', value: RecoveryStatus.RECOVERED },
  ];

  useEffect(() => {
    if (recovery !== null) {
      setAmount(recovery?.amount || 0);
      setSelectedRecoveryStatus(recovery?.status || RecoveryStatus.PENDING);
      setNotes(recovery?.notes || '');
    }
  }, [recovery]);

  const typeOptions = (
    <optgroup label="Recovery Status">
      {statuses.map((r, i) => (
        <option value={r.value} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );

  // Function to toggle the paid status of an order
  const changeRecoveryOnHistoryOrder = (
    recovery: {
      amount: number;
      status: RecoveryStatus;
      notes?: string | null;
    } | null
  ) => {
    const updatedOrders = dataHistory.map((orderItem) => {
      // Find the order to update
      if (orderItem.orders?.some((order) => order.id === id)) {
        return {
          ...orderItem,
          orders: orderItem.orders?.map((order) =>
            order.id === id ? { ...order, recovery: recovery } : order
          ),
        };
      }
      return orderItem;
    });
    // Update state with modified orders
    setDataHistory(updatedOrders);
  };

  async function postOrderRecovery() {
    if (!id) return;
    const body: MarkAsRecoveredRequestData = {
      amount,
      status: selectedRecoveryStatus,
      notes,
    };


    try {
      setLoading(true);
      const { data } = await orderRecovery(id, body);

      setLoading(false);
      setData((prev) => {
        if (!prev) return null;
        return { ...prev, recovery: body };
      });

      setDataOrders(
        dataOrders?.map((o) => {
          if (o.id === id) {
            return { ...o, recovery: body };
          }
          return o;
        })
      );

      changeRecoveryOnHistoryOrder(body);

      SuccessToast(data?.message || '');
      closeModal?.();
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function removeOrderRecovery() {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await deleteOrderRecovery(id);
      setLoading(false);
      setData((prev) => {
        if (!prev) return null;
        return { ...prev, recovery: null };
      });

      setDataOrders(
        dataOrders?.map((o) => {
          if (o.id === id) {
            return { ...o, recovery: null };
          }
          return o;
        })
      );

      changeRecoveryOnHistoryOrder(null);

      SuccessToast(data.message);
      closeModal?.();
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const handleAmountChange = useCallback((val: number) => {
    setAmount(val);
  }, []);

  return (
    <>
      {loading && <AdminSpinner />}

      <Modal isOpen={modalIsOpen} closeModal={closeModal} logo={false}>
        <Img $width="180px" src={moneyCoins} alt="recovery" />
        <H3 $mt="24">Recovery</H3>
        <Div $flex $column $gap="24px">
          <AdminSelect
            value={selectedRecoveryStatus}
            options={typeOptions}
            onChange={({ target }: { target: { value: string } }) =>
              setSelectedRecoveryStatus(target.value as RecoveryStatus)
            }
            placeholder="Select location"
            label="Choose Recovery Status"
          />

          <AdminDollarInput2
            label="Enter Recovery Amount"
            onValueChange={handleAmountChange}
            value={amount}
          />

          <AdminInput
            label="Notes"
            type="textarea"
            placeholder="Enter reason here"
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setNotes(e.target.value)
            }
            $textareaHeight={150}
          />
        </Div>

        {/* <Separator $m="24px" /> */}

        <P $m="0" $left $mt="24">
          Before you click on recovery button make sure that:
          <br />
          <br />
          1. Check if tablet is online (esper, wifi)
          <br />
          2. Is the merchant aware of this order <br />
          (ask why it was cancelled)
          <br />
          3. Check driver instructions for the store
        </P>

        <Button onClick={postOrderRecovery} $w="100%" $mt="24" $selected>
          Save
        </Button>
        {recovery !== null && (
          <Button
            onClick={removeOrderRecovery}
            $w="100%"
            $mt="16"
            $selected
            $gray
          >
            Remove From Recoveries
          </Button>
        )}
      </Modal>
    </>
  );
};
